import React, { memo, useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import _last from 'lodash/last';

import { feedback, video } from 'src/constants';
import { useAudio, useIntlMessages } from 'src/hooks';

import {
  Card,
  Container,
  FeedbackContainer,
  FeedbackPanel,
  FeedbackPreview,
  GameCoins,
  PageLoader,
  SentenceCreator,
  Navbar,
  Zoom,
  LevelContent,
  ReadGames,
  Topbar,
} from 'src/components/common';

import './index.scss';

const ReadTogetherLevelsContainer = ({
  collection,
  currency,
  handleLevelAnswer,
  handleCollectionEnd,
  hasLoaded,
  items,
  navigation,
  studentSpeechSound,
}) => {
  const messages = useIntlMessages();

  const [awardedSentenceIds, setAwardedSentenceIds] = useState([]);
  const [collectedCoins, setCollectedCoins] = useState(0);
  const [displayModal, setDisplayModal] = useState(false);
  const [level, setLevel] = useState(0);
  const [sentences, setSentences] = useState([]);
  const [playAudio, isAudioPlaying] = useAudio();

  const containerClassNames = useMemo(
    () =>
      cn(
        'read-together-levels-container',
        `read-together-levels-container--background-${studentSpeechSound.label}`
      ),
    [studentSpeechSound.label]
  );

  const handleAnswer = useCallback(
    (isCorrect) => {
      playAudio(feedback.COIN_COLLECT_SOUND_URL, () => {
        if (collectedCoins + 1 >= items.length && items.length > 0) {
          playAudio(feedback.LEVEL_END_SOUND_URL, handleCollectionEnd);
        }
      });

      if (level < items.length - 1) {
        if ((level + 1) % 3 === 0) {
          setSentences([]);
        }

        setAwardedSentenceIds([...awardedSentenceIds, items[level].id]);

        setLevel((prevLevel) => prevLevel + 1);
      }

      handleLevelAnswer(items[level].id, isCorrect, currency?.id);

      setCollectedCoins((prevCollectedCoins) => prevCollectedCoins + 1);
      setDisplayModal(false);
    },
    [
      awardedSentenceIds,
      collectedCoins,
      currency,
      handleCollectionEnd,
      handleLevelAnswer,
      items,
      level,
      playAudio,
    ]
  );

  const handleSentenceSelect = useCallback(
    (selectedSentenceId) => {
      if (isAudioPlaying) {
        return;
      }

      if (awardedSentenceIds.includes(selectedSentenceId)) {
        const selectedSentence = items.find(
          (item) => item.id === selectedSentenceId
        );

        if (selectedSentence.sentenceAudioUrl !== '') {
          playAudio(selectedSentence.sentenceAudioUrl);
        }

        return;
      }

      if (items[level].sentenceAudioUrl !== '') {
        setDisplayModal(false);
        playAudio(items[level].sentenceAudioUrl, () => {
          setDisplayModal(true);
        });
      } else {
        setDisplayModal(true);
      }
    },
    [
      awardedSentenceIds,
      setDisplayModal,
      isAudioPlaying,
      items,
      level,
      playAudio,
    ]
  );

  const playTitleAudio = useCallback(
    (titleAudioUrl) => {
      if (isAudioPlaying) {
        return;
      }

      if (titleAudioUrl !== '') {
        playAudio(titleAudioUrl);
      }
    },
    [isAudioPlaying, playAudio]
  );

  useEffect(() => {
    if (!hasLoaded) {
      setSentences([]);
    }

    if (hasLoaded) {
      const sentenceImages = items[level].sentenceImages.map(
        (sentenceImage) => ({
          imageUrl: sentenceImage.imageUrl,
          key: sentenceImage.key,
        })
      );

      const createdSentence = (
        <SentenceCreator
          imageClassNames="read-together-sentence-image"
          initialSentence={items[level].sentence}
          sentenceImages={sentenceImages}
        />
      );

      setSentences((prevSentences) => [
        ...prevSentences,
        { id: items[level].id, createdSentence },
      ]);
    }
  }, [hasLoaded, level, items]);

  if (!hasLoaded) {
    return <PageLoader />;
  }

  return (
    <PageLoader isFadingOut>
      {navigation}
      <Navbar.GameNavbar
        currency={currency}
        title={messages.game.readTogether}
        videoName={video.NAME.READ_TOGETHER}
      />
      <Topbar>
        <GameCoins
          coinSize="small"
          type={currency.key}
          totalCoinsCount={items.length}
          collectedCoinsCount={collectedCoins}
        />
      </Topbar>
      <Container className={containerClassNames}>
        <FeedbackContainer isVisible={displayModal}>
          <FeedbackPreview.Sound
            handleListenAgain={() => playAudio(items[level].sentenceAudioUrl)}
          >
            <LevelContent.Sentence>
              {!!sentences.length && _last(sentences).createdSentence}
            </LevelContent.Sentence>
          </FeedbackPreview.Sound>
          <FeedbackPanel.Grades onClick={handleAnswer} />
        </FeedbackContainer>

        <Zoom
          mobileWidth={320}
          mobileHeight={405}
          mobileRatio="calc(100% - 140px)"
          align="center"
        >
          <div className="read-together-levels-container__body">
            <Card
              className="read-together-levels-container__title"
              onClick={() => playTitleAudio(collection.titleAudioUrl)}
            >
              {collection.title}
            </Card>
            <ReadGames.Sentences
              sentencesCount={items.length}
              screenSentences={sentences}
              onClick={handleSentenceSelect}
              level={level}
            />
          </div>
        </Zoom>
      </Container>
    </PageLoader>
  );
};

ReadTogetherLevelsContainer.propTypes = {
  collection: PropTypes.shape({
    title: PropTypes.string,
    titleAudioUrl: PropTypes.string,
  }),
  currency: PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string,
    imageUrl: PropTypes.string,
    placeholderImageUrl: PropTypes.string,
  }),
  handleCollectionEnd: PropTypes.func.isRequired,
  handleLevelAnswer: PropTypes.func.isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      sentence: PropTypes.string.isRequired,
      sentenceAudioUrl: PropTypes.string.isRequired,
      sentenceImages: PropTypes.arrayOf(
        PropTypes.shape({
          imageUrl: PropTypes.string.isRequired,
        })
      ),
    })
  ),
  navigation: PropTypes.node,
  studentSpeechSound: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

ReadTogetherLevelsContainer.defaultProps = {
  navigation: null,
  collection: null,
  currency: null,
  items: [],
};

export default memo(ReadTogetherLevelsContainer);
